"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { identifyLocalMp3 } from "@/lib/acoustid";

export type LocalMp3Track = {
  id: string;
  fileName: string;
  file: File;
  title: string;
  recognizedTitle: string | null;
  artist: string | null;
  manualTitle: string;
  manualArtist: string;
  objectUrl: string;
  sizeBytes: number;
  durationMs: number | null;
  recognitionStatus: "idle" | "recognizing" | "recognized" | "not_found" | "failed";
};

type AddLocalMp3FilesResult = {
  addedCount: number;
  rejectedCount: number;
};

function createTrackId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function titleFromFileName(fileName: string) {
  const withoutExtension = fileName.replace(/\.mp3$/i, "").trim();
  return withoutExtension || "Untitled MP3";
}

function isMp3File(file: File) {
  const normalizedType = file.type.trim().toLowerCase();
  return (
    /\.mp3$/i.test(file.name) ||
    normalizedType === "audio/mpeg" ||
    normalizedType === "audio/mp3"
  );
}

function mediaErrorMessage(audio: HTMLAudioElement) {
  switch (audio.error?.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return "MP3 playback was interrupted.";
    case MediaError.MEDIA_ERR_NETWORK:
      return "The browser could not read this MP3.";
    case MediaError.MEDIA_ERR_DECODE:
      return "The selected file could not be decoded as an MP3.";
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return "This MP3 encoding is not supported by the browser.";
    default:
      return "The selected MP3 could not be played.";
  }
}

export function useLocalMp3Player() {
  const [tracks, setTracks] = useState<LocalMp3Track[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [error, setError] = useState<string | null>(null);
  const [recognitionError, setRecognitionError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tracksRef = useRef<LocalMp3Track[]>([]);
  const currentIndexRef = useRef<number | null>(null);
  const playTrackAtRef = useRef<(index: number) => Promise<void>>(async () => {});
  const identificationInProgressRef = useRef(new Set<string>());
  const identificationCompleteRef = useRef(new Set<string>());

  const replaceTracks = useCallback((nextTracks: LocalMp3Track[]) => {
    tracksRef.current = nextTracks;
    setTracks(nextTracks);
  }, []);

  const identifyTrack = useCallback(
    async (track: LocalMp3Track) => {
      if (
        identificationInProgressRef.current.has(track.id) ||
        identificationCompleteRef.current.has(track.id)
      ) {
        return;
      }

      identificationInProgressRef.current.add(track.id);
      setRecognitionError(null);
      replaceTracks(
        tracksRef.current.map((candidate) =>
          candidate.id === track.id
            ? { ...candidate, recognitionStatus: "recognizing" }
            : candidate,
        ),
      );

      try {
        const match = await identifyLocalMp3(track.file);
        identificationCompleteRef.current.add(track.id);
        if (!match) {
          replaceTracks(
            tracksRef.current.map((candidate) =>
              candidate.id === track.id
                ? { ...candidate, recognitionStatus: "not_found" }
                : candidate,
            ),
          );
          return;
        }

        replaceTracks(
          tracksRef.current.map((candidate) =>
            candidate.id === track.id
              ? {
                  ...candidate,
                  recognizedTitle: match.title,
                  artist: match.artist,
                  recognitionStatus: "recognized" as const,
                }
              : candidate,
          ),
        );
      } catch (recognitionFailure) {
        replaceTracks(
          tracksRef.current.map((candidate) =>
            candidate.id === track.id
              ? { ...candidate, recognitionStatus: "failed" }
              : candidate,
          ),
        );
        setRecognitionError(
          recognitionFailure instanceof Error
            ? recognitionFailure.message
            : "Music recognition failed.",
        );
      } finally {
        identificationInProgressRef.current.delete(track.id);
      }
    },
    [replaceTracks],
  );

  const setActiveIndex = useCallback((index: number | null) => {
    currentIndexRef.current = index;
    setCurrentIndex(index);
  }, []);

  const updateTrackDuration = useCallback((trackId: string, nextDurationMs: number) => {
    const nextTracks = tracksRef.current.map((track) =>
      track.id === trackId
        ? {
            ...track,
            durationMs: nextDurationMs,
          }
        : track,
    );
    replaceTracks(nextTracks);
  }, [replaceTracks]);

  const loadTrackAt = useCallback(
    async (index: number, play: boolean) => {
      const audio = audioRef.current;
      const track = tracksRef.current[index];
      if (!audio || !track) return;

      setError(null);
      setActiveIndex(index);
      setPositionMs(0);
      setDurationMs(track.durationMs ?? 0);

      if (audio.src !== track.objectUrl) {
        audio.pause();
        audio.src = track.objectUrl;
        audio.load();
      }

      if (!play) return;

      try {
        await audio.play();
      } catch (playError) {
        const message =
          playError instanceof DOMException && playError.name === "NotAllowedError"
            ? "Select play again to allow audio playback."
            : playError instanceof Error
              ? playError.message
              : "The selected MP3 could not be played.";
        setError(message);
      }
    },
    [setActiveIndex],
  );

  const playTrackAt = useCallback(
    async (index: number) => {
      await loadTrackAt(index, true);
    },
    [loadTrackAt],
  );

  useEffect(() => {
    playTrackAtRef.current = playTrackAt;
  }, [playTrackAt]);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "metadata";
    audio.volume = 0.8;
    audioRef.current = audio;

    const syncPosition = () => {
      setPositionMs(Math.max(0, audio.currentTime * 1000));
    };
    const syncDuration = () => {
      if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
      const nextDurationMs = Math.round(audio.duration * 1000);
      setDurationMs(nextDurationMs);

      const index = currentIndexRef.current;
      const track = index == null ? null : tracksRef.current[index];
      if (track && track.durationMs !== nextDurationMs) {
        updateTrackDuration(track.id, nextDurationMs);
      }
    };
    const handlePlay = () => {
      setIsPlaying(true);
      const index = currentIndexRef.current;
      const track = index == null ? null : tracksRef.current[index];
      if (track) {
        void identifyTrack(track);
      }
    };
    const handlePause = () => setIsPlaying(false);
    const handleError = () => {
      setIsPlaying(false);
      setError(mediaErrorMessage(audio));
    };
    const handleEnded = () => {
      const index = currentIndexRef.current;
      const trackCount = tracksRef.current.length;
      if (index == null || trackCount === 0) {
        setIsPlaying(false);
        return;
      }

      const nextIndex = index + 1;
      if (nextIndex < trackCount) {
        void playTrackAtRef.current(nextIndex);
        return;
      }

      setIsPlaying(false);
      setPositionMs(
        Number.isFinite(audio.duration) ? Math.max(0, audio.duration * 1000) : 0,
      );
    };

    audio.addEventListener("timeupdate", syncPosition);
    audio.addEventListener("loadedmetadata", syncDuration);
    audio.addEventListener("durationchange", syncDuration);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audio.removeEventListener("timeupdate", syncPosition);
      audio.removeEventListener("loadedmetadata", syncDuration);
      audio.removeEventListener("durationchange", syncDuration);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("ended", handleEnded);
      tracksRef.current.forEach((track) => URL.revokeObjectURL(track.objectUrl));
      audioRef.current = null;
    };
  }, [identifyTrack, updateTrackDuration]);

  const addFiles = useCallback(
    (fileList: FileList | File[]): AddLocalMp3FilesResult => {
      const selectedFiles = Array.from(fileList);
      const acceptedFiles = selectedFiles.filter(isMp3File);
      const rejectedCount = selectedFiles.length - acceptedFiles.length;

      if (acceptedFiles.length === 0) {
        if (rejectedCount > 0) {
          setError("Choose an MP3 file.");
        }
        return { addedCount: 0, rejectedCount };
      }

      const addedTracks = acceptedFiles.map((file): LocalMp3Track => ({
        id: createTrackId(),
        fileName: file.name,
        file,
        title: titleFromFileName(file.name),
        recognizedTitle: null,
        artist: null,
        manualTitle: "",
        manualArtist: "",
        objectUrl: URL.createObjectURL(file),
        sizeBytes: file.size,
        durationMs: null,
        recognitionStatus: "idle",
      }));
      const firstAddedIndex = tracksRef.current.length;
      const nextTracks = [...tracksRef.current, ...addedTracks];

      replaceTracks(nextTracks);
      setError(
        rejectedCount > 0
          ? `${rejectedCount} non-MP3 ${rejectedCount === 1 ? "file was" : "files were"} skipped.`
          : null,
      );

      if (currentIndexRef.current == null) {
        void loadTrackAt(firstAddedIndex, false);
      }

      return {
        addedCount: addedTracks.length,
        rejectedCount,
      };
    },
    [loadTrackAt, replaceTracks],
  );

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (currentIndexRef.current == null) {
      if (tracksRef.current.length > 0) {
        await playTrackAt(0);
      }
      return;
    }

    if (audio.paused) {
      try {
        setError(null);
        await audio.play();
      } catch (playError) {
        setError(
          playError instanceof Error
            ? playError.message
            : "The selected MP3 could not be played.",
        );
      }
      return;
    }

    audio.pause();
  }, [playTrackAt]);

  const nextTrack = useCallback(async () => {
    const trackCount = tracksRef.current.length;
    if (trackCount === 0) return;
    const current = currentIndexRef.current ?? -1;
    await playTrackAt((current + 1 + trackCount) % trackCount);
  }, [playTrackAt]);

  const previousTrack = useCallback(async () => {
    const audio = audioRef.current;
    const trackCount = tracksRef.current.length;
    if (!audio || trackCount === 0) return;

    if (audio.currentTime > 3) {
      audio.currentTime = 0;
      setPositionMs(0);
      return;
    }

    const current = currentIndexRef.current ?? 0;
    await playTrackAt((current - 1 + trackCount) % trackCount);
  }, [playTrackAt]);

  const seek = useCallback((nextPositionMs: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(nextPositionMs)) return;
    const nextSeconds = Math.max(
      0,
      Math.min(nextPositionMs / 1000, Number.isFinite(audio.duration) ? audio.duration : Infinity),
    );
    audio.currentTime = nextSeconds;
    setPositionMs(nextSeconds * 1000);
  }, []);

  const setVolume = useCallback((nextVolume: number) => {
    const normalizedVolume = Math.max(0, Math.min(1, nextVolume));
    if (audioRef.current) {
      audioRef.current.volume = normalizedVolume;
    }
    setVolumeState(normalizedVolume);
  }, []);

  const removeTrack = useCallback(
    (trackId: string) => {
      const removedIndex = tracksRef.current.findIndex((track) => track.id === trackId);
      if (removedIndex < 0) return;

      const removedTrack = tracksRef.current[removedIndex];
      const activeIndex = currentIndexRef.current;
      const removingActiveTrack = activeIndex === removedIndex;
      const audio = audioRef.current;

      if (removingActiveTrack && audio) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }

      const nextTracks = tracksRef.current.filter((track) => track.id !== trackId);
      replaceTracks(nextTracks);
      URL.revokeObjectURL(removedTrack.objectUrl);
      identificationInProgressRef.current.delete(removedTrack.id);
      identificationCompleteRef.current.delete(removedTrack.id);

      if (nextTracks.length === 0) {
        setActiveIndex(null);
        setPositionMs(0);
        setDurationMs(0);
        setError(null);
        return;
      }

      if (removingActiveTrack) {
        const nextIndex = Math.min(removedIndex, nextTracks.length - 1);
        void loadTrackAt(nextIndex, false);
      } else if (activeIndex != null && removedIndex < activeIndex) {
        setActiveIndex(activeIndex - 1);
      }
    },
    [loadTrackAt, replaceTracks, setActiveIndex],
  );

  const clearTracks = useCallback(() => {
    const audio = audioRef.current;
    audio?.pause();
    if (audio) {
      audio.removeAttribute("src");
      audio.load();
    }
    tracksRef.current.forEach((track) => URL.revokeObjectURL(track.objectUrl));
    identificationInProgressRef.current.clear();
    identificationCompleteRef.current.clear();
    replaceTracks([]);
    setActiveIndex(null);
    setPositionMs(0);
    setDurationMs(0);
    setError(null);
    setRecognitionError(null);
  }, [replaceTracks, setActiveIndex]);

  const retryRecognition = useCallback(
    (trackId: string) => {
      const track = tracksRef.current.find((candidate) => candidate.id === trackId);
      if (!track) return;
      identificationCompleteRef.current.delete(trackId);
      void identifyTrack(track);
    },
    [identifyTrack],
  );

  const updateManualMetadata = useCallback(
    (
      trackId: string,
      field: "manualTitle" | "manualArtist",
      value: string,
    ) => {
      replaceTracks(
        tracksRef.current.map((track) =>
          track.id === trackId ? { ...track, [field]: value } : track,
        ),
      );
    },
    [replaceTracks],
  );

  const currentTrack =
    currentIndex == null ? null : (tracks[currentIndex] ?? null);

  return {
    tracks,
    currentTrack,
    currentIndex,
    isPlaying,
    positionMs,
    durationMs,
    volume,
    error,
    recognitionError,
    addFiles,
    playTrackAt,
    togglePlay,
    nextTrack,
    previousTrack,
    seek,
    setVolume,
    removeTrack,
    clearTracks,
    retryRecognition,
    updateManualMetadata,
  };
}
